/**
 * PipelinePage — operators with unbilled tickets + the Generate-invoice action.
 *
 * Phase 12 / Task 12.3. Reads GET /v1/billing/pipeline (event count + total cents
 * per operator) and drives POST /v1/billing/invoices/generate for a shared
 * billing period (defaults to the current calendar month). On success the
 * operator's tickets move TO_BE_INVOICED→INVOICED and drop off the pipeline.
 *
 * The frontend gate is UX-only — the API enforces VIEW_INVOICES / CREATE_INVOICE
 * (three-layer security). Money via formatUsdFromCents; no bare UTC anywhere.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { SortableHead } from 'src/components/ui/sortable-head';

import { formatUsdFromCents } from '../lib/money';
import { sortRows, useUrlSort } from '../lib/url-state';
import { periodFromMonthValue, useBillingMonth } from '../lib/billing-period';
// The row key, also the drill-down URL segment: cannot collide across the two identities.
import { customerKey } from '../lib/customer-key';
import {
  useBillingPipeline,
  useGenerateInvoice,
  type GenerateInvoiceCustomer,
  type GenerateInvoiceResponse,
  type PipelineOperator,
} from '../features/billing/hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

/**
 * The identity "Generate invoice" posts for a row (86bc77y16, DEC-192 option A).
 * Current-model customers carry `haulerOperatorId` and a NULL `operatorId` — gating on
 * `operatorId` alone hid the button for every customer set up the current way. The
 * current key wins when both are present: the generator's legacy arm only bills events
 * whose hauler_operator_id IS NULL, so posting `operatorId` for such a row bills nothing.
 * Null = no customer identity at all, which cannot be invoiced.
 */
function pipelineCustomer(row: PipelineOperator): GenerateInvoiceCustomer | null {
  if (row.haulerOperatorId !== null) return { haulerOperatorId: row.haulerOperatorId };
  if (row.operatorId !== null) return { operatorId: row.operatorId };
  return null;
}

/** The columns the Pipeline table sorts by (OPEN-204); anything else in ?sort= is ignored. */
const PIPELINE_SORTS = ['operator', 'tickets', 'total'] as const;

export function PipelinePage(): React.JSX.Element {
  // In the URL, so a customer's drill-down opens on the same month (#50).
  const [monthValue, setMonthValue] = useBillingMonth();
  const period = periodFromMonthValue(monthValue);

  // Which customer row is currently generating (the mutation is page-level; we
  // scope the spinner + disabled state to the clicked row).
  const [pendingCustomerKey, setPendingCustomerKey] = React.useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useBillingPipeline();

  const generate = useGenerateInvoice((result: GenerateInvoiceResponse) => {
    setPendingCustomerKey(null);
    if (result.created) {
      toast.success('Invoice generated');
    } else {
      toast.info('Nothing to bill for this operator and period');
    }
  });

  const handleGenerate = React.useCallback(
    (customer: GenerateInvoiceCustomer): void => {
      if (period === null) {
        toast.error('Choose a valid billing month first');
        return;
      }
      setPendingCustomerKey(customerKey(customer));
      generate.mutate(
        { ...customer, periodStart: period.periodStart, periodEnd: period.periodEnd },
        {
          onError: () => {
            setPendingCustomerKey(null);
            toast.error('Could not generate the invoice. Try again.');
          },
        },
      );
    },
    [generate, period],
  );

  // OPEN-204 — click a header to sort; the whole pipeline is loaded, so this sorts every row.
  const { sort, toggle } = useUrlSort(PIPELINE_SORTS, { key: 'operator', dir: 'asc' }, []);
  const rows: readonly PipelineOperator[] = sortRows(data?.pipeline ?? [], sort, (r, k) => {
    switch (k) {
      case 'operator':
        return r.operatorName;
      case 'tickets':
        return r.eventCount;
      case 'total':
        return r.totalCents;
    }
  });

  return (
    <div style={{ padding: '24px', maxWidth: '960px' }}>
      {/* Billing-period control — shared across the page (Task 12.3). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <label
          htmlFor="billing-month"
          style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}
        >
          Billing period
        </label>
        <input
          id="billing-month"
          type="month"
          value={monthValue}
          onChange={(e): void => {
            setMonthValue(e.target.value);
          }}
          className="[color-scheme:dark]"
          style={{
            height: '40px',
            borderRadius: '10px',
            border: '1px solid var(--color-border-secondary)',
            backgroundColor: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-primary)',
            padding: '0 12px',
            fontSize: '14px',
          }}
        />
      </div>

      {isLoading && (
        <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading pipeline…
        </p>
      )}

      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load the billing pipeline.</p>
          <Button
            variant="outline"
            onClick={(): void => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p style={{ color: 'var(--color-text-secondary)' }}>
          No unbilled tickets. Everything in this workspace is invoiced.
        </p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div
          style={{
            borderRadius: 'var(--radius-card)',
            backgroundColor: 'var(--color-bg-secondary)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="operator" sort={sort} onSort={toggle}>
                  Operator
                </SortableHead>
                <SortableHead className="text-right" column="tickets" sort={sort} onSort={toggle}>
                  Tickets
                </SortableHead>
                <SortableHead className="text-right" column="total" sort={sort} onSort={toggle}>
                  Unbilled total
                </SortableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const customer = pipelineCustomer(row);
                const key =
                  customer !== null
                    ? customerKey(customer)
                    : `external-${row.operatorName ?? 'unknown'}`;
                const isRowPending = customer !== null && pendingCustomerKey === key;
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {customer !== null ? (
                        // #50 — the customer's tickets, on the same month.
                        <Link
                          to={`/pipeline/${key}?month=${monthValue}`}
                          style={{ color: 'var(--color-link)', fontWeight: 500 }}
                        >
                          {row.operatorName ?? 'Unnamed customer'}
                        </Link>
                      ) : (
                        (row.operatorName ?? (
                          <span style={{ color: 'var(--color-text-tertiary)' }}>
                            Unmapped operator
                          </span>
                        ))
                      )}
                      {/* #50 PR C — tickets billing edited (DEC-356). */}
                      {row.editedTicketCount > 0 && (
                        <Badge variant="secondary" style={{ marginLeft: '8px' }}>
                          Edited ({formatNumber(row.editedTicketCount)})
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(row.eventCount)}</TableCell>
                    <TableCell className="text-right">
                      {formatUsdFromCents(row.totalCents)}
                    </TableCell>
                    <TableCell className="text-right">
                      {customer !== null ? (
                        <Button
                          onClick={(): void => {
                            handleGenerate(customer);
                          }}
                          disabled={generate.isPending}
                        >
                          {isRowPending && (
                            <Loader2
                              size={14}
                              aria-hidden="true"
                              style={{ marginRight: '6px' }}
                              className="animate-spin"
                            />
                          )}
                          Generate invoice
                        </Button>
                      ) : (
                        <span
                          title="These tickets have no customer — can't invoice automatically."
                          style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}
                        >
                          No customer record
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
